// @ts-check
import api from './api'
import {isEmpty, prop} from 'ramda'
import Debug from 'debug'
const debug = Debug('app:shared:companies')


/**
  * @typedef MyCompany
  * @property {string} companyCity
  * @property {number} companyId
  * @property {string} companyLogoUrl
  * @property {string} companyName
  * @property {string} companyExternalId
*/


const companies = ($http, $q, Auth) => {
  'ngInject'

  let jobTypes = []
  let positionsData = {}
  let companiesData = {}
  let childCompaniesData = {}
  let companyData
  let invoiceData = {}
  let usersData = {}
  let usersOwnCompaniesData = {}

  /**
   * Invites client to company
   * @param {string} companyId - external id of company
   * @param {string} firstName - first name of user
   * @param {string} lastName - last name of user
   * @param {string} username - email of user to add
   * @return {null} Success/Error confimation
   */
  const inviteCompanyUser = (data) => {
    debug('creating template %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/sendInvite`,
      data: {
        companyId: data.companyId,
        username: data.username,
        firstName: data.firstName,
        lastName: data.lastName,
        userType: data.userType,
        phoneNumber: data.phoneNumber || null,
        region: data.region || null
      }
    })
    .then(null, (err) => { throw err })
  }

  const uploadLogo = (data) => {
    debug('update company logo %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/files/upload/logo`,
      data: {
        logo_base64: data.logo_base64,
        returnInternalId: true
      }
    })
    .then(prop('data'), (err) => { throw err })
  }

  const addUserToCompany = (data) => {
    debug('Adding User to Company %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/addToCompany`,
      data: {
        companyId: data.companyId,
        username: data.username
      }
    })
    .then(null, (err) => { throw err })
  }

  const resendInvite = (data) => {
    debug('resending invite %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBaseV2()}/user_management/resendAccountInvite`,
      data: {
        email: data.email
      }
    })
    .then(null, (err) => { throw err })
  }

  const createCompany = (data) => {
    debug('creating company %o', data)
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/company/create`,
      data: {
        companyInfo: data.companyInfo
      }
    })
    .then(null, (err) => { throw err })
  }

  const updateCompany = (data) => {
    debug('update company %o', data)
    return $http({
      method: 'PATCH',
      contentType: 'application/json',
      url: `${api.getBaseV2()}/company/editFields`,
      data: {
        companyInfo: data.companyInfo
      }
    })
    .then(null, (err) => { throw err })
  }

  const updateUser = (data) => {
    // TODO:(BW-1015) changes here
    debug('updating user %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/editClientType`,
      data: {
        userId: data.userId,
        clientType: data.clientType
      }
    })
    .then(null, (err) => { throw err })
  }

  const updateBillingInfo = (companyId, primaryContactFirstName, primaryContactLastName, primaryContactEmail, additionalEmails, purchaseOrder, invoiceDay) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/companies/${companyId}/updateBillingInfo`,
      data: {
        primaryContactFirstName,
        primaryContactLastName,
        primaryContactEmail,
        additionalEmails,
        purchaseOrder,
        invoiceDay
      }
    })
    .then(null, (err) => { throw err })
  }

  const updateEmailPreference = (data) => {
    debug('updating user %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/editEmailPreference`,
      data: {
        userId: data.userId,
        emailCadence: data.emailCadence
      }
    })
    .then(null, (err) => { throw err })
  }

  const fetchCompanyUsers = (companyId, userTypeGroup) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/users/getUsers`,
      data: {
        companyId: companyId,
        userTypeGroup: userTypeGroup
      }
    })
    .then(prop('data'))
    .then(setUsersData)
  }

  const setUsersData = (response) => {
    usersData = response
    return usersData
  }

  const fetchAdmins = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/company/users/getAdmins`
    })
    .then(prop('data'), (err) => { throw err })
    .then(setAdmins)
  }

  const setAdmins = (response) => {
    usersData = response
    return usersData
  }

  // returns expanded list of admin users (utype in 1,5,7,8) for Manage Jobs page
  const fetchAdminsMJ = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/company/users/getAdminsMJ`
    })
    .then(prop('data'), (err) => { throw err })
    .then(response => response)
  }

  /**
   * Fetch companies a user is allowed to switch to
   * For super admins and bluecrew admins, returns all companies.
   * For Branch ops / vis returns their associated companies
   * etc
   * @returns {Promise<{ result: MyCompany[] }> }
   */
  const getMyCompanies = () => {
    return $http({
      method: 'GET',
      url: `${api.getBaseV2()}/users/myCompanies`,
    })
    .then(prop('data'))
    .then(setCompaniesData)
  }

  // For Admins to get Client List TODO Add filtering based on Account Manager
  const getCompaniesData = (params) => {
    if (!isEmpty(companiesData)) {
      let def = $q.defer()
      def.resolve(companiesData)
      return def.promise
    }

    const requestData = typeof params === 'undefined' ? {} : params

    return fetchCompaniesData(requestData)
  }

  const fetchCompaniesData = (params) => {
    if (companiesData.companies) {
      return Promise.resolve(companiesData)
    }
    return $http({
      method: 'GET',
      url: `${api.getBase()}/companies`,
      params: params
    })
    .then(prop('data'))
    .then(setCompaniesData)
  }

  const setCompaniesData = (response) => {
    companiesData = response
    return companiesData
  }

  // For Client Admins with different locations who want to be able to switch to other child companies
  const getChildCompaniesData = (parentCompanyId) => {
    if (!isEmpty(childCompaniesData)) {
      let def = $q.defer()
      def.resolve(childCompaniesData)
      return def.promise
    }
    return fetchChildCompaniesData(parentCompanyId)
  }

  const fetchChildCompaniesData = (parentCompanyId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/getChildCompanies`,
      data: {
        parentCompanyId: parentCompanyId
      }

    })
    .then(prop('data'))
    .then(setChildCompaniesData)
  }

  const setChildCompaniesData = (response) => {
    childCompaniesData = response
    return childCompaniesData
  }

  const getPositionsData = (companyId) => {
    if (!isEmpty(positionsData)) {
      let def = $q.defer()
      def.resolve(positionsData)
      return def.promise
    }
    return fetchPositionsData(companyId)
  }

  // For Current Branch Companies data
  const getCompanyData = () => {
    if (companyData) {
      return Promise.resolve(companyData)
    }
    return $http({
      method: 'GET',
      url: `${api.getBase()}/company/get`
    })
    .then(prop('data'))
    .then(setCompanyData)
  }

  const setCompanyData = (response) => {
    companyData = response
    return companyData
  }

  const getCompany = (id) => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/company/get`,
      params: {
        companyId: id
      }
    })
    .then(response => {
      return response.data
    })
  }

  const adminFetchCompany = (companyId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/get`,
      data: {
        companyId: companyId
      }
    })
    .then(prop('data'))
    .then(setCompanyData)
  }

  const fetchInvoicePDF = (invoiceId) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/invoices/getPdf`,
      data: {
        invoiceId: invoiceId
      },
      responseType: 'arraybuffer'
    })
  }

  const fetchAttachments = (invoiceId) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/invoices/findAttachables`,
      data: {
        invoiceId: invoiceId
      }
    })
      .then(prop('data'))
  }

  const getInvoiceData = (customerId) => {
    if (invoiceData.length) {
      let def = $q.defer()
      def.resolve(invoiceData)
      return def.promise
    }
    return fetchInvoiceData(customerId)
  }

  const fetchInvoiceData = (customerId) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/invoices/get`,
      data: {
        customerId: JSON.stringify(customerId)
      }
    })
    .then(prop('data'), (err) => { throw err })
    .then(setInvoiceData)
  }

  const setInvoiceData = (response) => {
    debug('set Invoice %o', response)
    invoiceData = response
    debug(invoiceData)
    return invoiceData
  }

  const fetchPositionsData = (companyId) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/routes/getPositions`,
      data: {
        companyId: companyId
      }
    })
    .then(prop('data'))
    .then(setPositionData)
  }

  const fetchBillerClient = (companyId) => {
    return $http({
      method: 'GET',
      url: `${api.baseBiller}/biller/client?workplaceId=` + companyId,
    })
        .then(prop('data'))
  }
  const fetchBillerPlans = (companyId) => {
    return $http({
      method: 'GET',
      url: `${api.baseBiller}/biller/` + companyId + `/billing_plan`,
    })
        .then(prop('data'))
  }
  const setPositionData = (response) => {
    positionsData = response
    debug(positionsData)
    return positionsData
  }

  const getJobTypes = () => {
    if (jobTypes.length) {
      Promise.resolve(jobTypes)
    }
    return $http({
      method: 'GET',
      url: `${api.getBase()}/job/getTypes`
    })
    .then(prop('data'))
  }

  const fetchJobTypes = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/job/getTypes`
    })
    .then(prop('data'), (err) => { throw err })
  }

  const fetchLeads = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/leads`
    })
    .then(prop('data'))
  }

  const getTerminalCode = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/company/getTerminalCode`
    })
    .then(prop('data'))
  }

  const fetchAnalytics = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/analytics/companyAnalytics`
    })
    .then(prop('data'))
  }

  const migrateV3Company = (companyIds) => {
    return $http({
      method: 'POST',
      url: `${api.getBaseV2()}/admin/v1_migration`,
      data: {
        company_ids: companyIds
      }
    })
    .then(prop('data'))
  }

  const createCostCenter1 = (workplaceId, businessUnit, regionId, productCode, name) => {
    return $http({
      method: 'PUT',
      contentType: 'application/json',
      url: `${api.basePayrollPortal}/ccids-mappings/company-updated`,
      data: {
        workplaceId,
        businessUnit,
        regionId,
        productCode,
        name
      }
    })
    .then(prop('data'))
  }

  const getCompanyCCIDsMappings = (workplaceId) => {
    return $http({
      method: 'GET',
      url: `${api.basePayrollPortal}/ccids-mappings?workplace_id=${workplaceId}`
    })
    .then(prop('data'))
  }

  // fetch customer magic link
  const fetchMagicLink = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/invoices/magicLink`
    }).then((resp) => {
      return resp.data.url
    }, (err) => {
      throw err
    })
  }

  /**
   * Download the roster as a PDF or CSV for a given date or date range.
   * filename can be a date like '2023-09-18'
   * or a range like 2023-09-18_to_2023-09-25'
   * @param {string} fileName the date as a string, or a date range
   * @param {'pdf' | 'csv'} fileType
   * @returns The roster as a pdf or csv
   */
  const getRoster = (fileName, fileType) => {
    let url = `${api.getBase()}/roster/${fileName}.${fileType}`
    return $http({
      method: 'GET',
      url,
      responseType: 'arraybuffer'
    })
  };

  return {
    fetchBillerClient,
    fetchBillerPlans,
    updateEmailPreference,
    updateBillingInfo,
    getPositionsData,
    getCompaniesData,
    getChildCompaniesData,
    getMyCompanies,
    getCompanyData,
    updateUser,
    uploadLogo,
    getInvoiceData,
    fetchInvoicePDF,
    fetchAnalytics,
    getCompany,
    adminFetchCompany,
    addUserToCompany,
    resendInvite,
    inviteCompanyUser,
    fetchAttachments,
    fetchAdmins,
    fetchAdminsMJ,
    fetchCompanyUsers,
    getJobTypes,
    createCompany,
    updateCompany,
    fetchLeads,
    getTerminalCode,
    migrateV3Company,
    createCostCenter1,
    getCompanyCCIDsMappings,
    fetchMagicLink,
    getRoster,
  }
}

export default companies
